<script setup>
import { onMounted, ref, useTemplateRef, computed } from 'vue';
import { useContentStore } from '../stores/contentStore';
import Image from '../components/Common/Image.vue';
import { useRoute } from 'vue-router';
import Icon from '../components/Common/Icon.vue';
import { gsap } from 'gsap';
import { useLocalizationStore } from '../stores/localizationStore';

const route = useRoute();
const contentStore = useContentStore();
const category = ref(contentStore.content.categories[route.params.categoryId]);

const { $trans, $localize } = useLocalizationStore();

const cards = useTemplateRef('cards');

const stories = computed(() => {
    return Object.values(category.value.stories).map(story => story.self).sort(function(a, b) {
        if (a.sortkey < b.sortkey) return 1;
        if (a.sortkey > b.sortkey) return -1;
        return 0;
    });
})

onMounted(() => {
    // Only animate first 4 cards due to performance
    const first4Cards = Array.from(cards.value.querySelectorAll('.card')).slice(0, 4);
    gsap.from(first4Cards, {
        opacity: 0,
        x: 100,
        duration: 1,
        stagger: 0.1,
        ease: "power2.out",
        onStart: function(){
            this.targets().forEach(target => target.classList.remove('transition'))
        },
        onComplete: function(){
            gsap.set(this.targets(), { clearProps: "all" });
            this.targets().forEach(target => target.classList.add('transition'))
        }
    })
})

function updateCardOffset(){
    const headerHeight = document.querySelector('header.Header').getBoundingClientRect().height;

    if(!cards.value){
        return;
    }

    cards.value.querySelectorAll('.card').forEach(card => {
        const bounding = card.getBoundingClientRect();

        card.classList.toggle('opacity-0', bounding.top < headerHeight - 40);
        card.classList.toggle('translate-x-40', bounding.top < headerHeight - 40);
    });
}

document.addEventListener('scroll', () => {
    updateCardOffset();
}, {passive: true})
</script>

<template>
    <div class="grid grid-cols-3 gap-x-24 xl:gap-x-48 pt-52 p-10 4k:p-16 4k:pt-72">
        <div class="sticky top-56 4k:top-72 h-fit pt-10 4k:pt-36">
            <Image class="mb-16 xl:mb-32 4k:mb-60 h-36 xl:h-48 4k:h-72 mx-auto" :image="category.self.image" />
            <div class="h-px w-full bg-white mb-7 xl:mb-14"></div>
            <h1 class="r-text-43 font-medium mb-4 xl:mb-8 4k:mb-10" v-text="$localize(category.self.intro.title)"></h1>
            <p class="r-text-28" v-text="$localize(category.self.intro.text)"></p>
        </div>
        <div ref="cards" class="space-y-10 text-white col-span-2">
            <div
                v-for="story in stories"
                :key="story.id"
                class="card transition rounded-lg relative bg-white/20 hover:bg-secondary/20 backdrop-blur-2xl p-6 xl:p-10 4k:p-16 flex items-start gap-x-10 xl:gap-x-20"
            >
                <Image class="w-40 xl:w-80 4k:w-96 shrink-0" :image="story.image" />
                <div class="grow xl:contents">
                    <div class="mb-5 xl:mb-0 grow">
                        <h2 class="r-text-43 font-medium mb-3 xl:mb-5 4k:mb-10" v-text="$localize(story.title)"></h2>
                        <p class="r-text-28" v-text="$localize(story.teaser)"></p>
                    </div>
                    <router-link
                        :to="{name: 'story', params: {categoryId: category.self.id, storyId: story.id}}"
                        class="expand-clickable-area shrink-0 uppercase font-bold r-text-18 flex gap-x-2 self-end"
                    >
                        <span v-text="$trans('story.view-more')"></span>
                        <Icon name="arrow-right-small" class="size-3 mt-1 4k:mt-1.5" />
                    </router-link> 
                </div>
            </div>
        </div>
    </div>
</template>
